import { TextField } from "@fluentui/react";
import React, { FormEventHandler, Fragment, ReactElement, useCallback } from "react";
import { localize } from "src/l10n";
import { FormControl } from "src/ui/components/Forms";
import { PlannerItemFormProps } from "./PlannerItemForm.types";
import { PlannerModel } from "../types";
import { SpintrTypes } from "src/typings";
import { PlannerFilePicker } from "../PlannerFilePicker";
import { getAvailableFileTypes, PlannerFileTypes } from "../utils";

function PlannerCourseForm(props: PlannerItemFormProps<Spintr.PlannerCourse>): ReactElement {
    const { errors, model, onModelUpdate } = props

    const onTextFieldChanged = useCallback<FormEventHandler<HTMLInputElement | HTMLTextAreaElement>>(
        (event) => {
            if (!event?.target) {
                return;
            }

            const { name, value } = (event.target as HTMLInputElement | HTMLTextAreaElement);

            onModelUpdate((prevModel) => Object.assign(
                { ...prevModel },
                { [name]: value },
            ) as PlannerModel);
        },
        [onModelUpdate],
    );

    if (model.itemType !== SpintrTypes.PlannerItemType.Course) {
        return null;
    }

    return (
        <Fragment>
            <FormControl>
                <TextField
                    aria-required={false}
                    className="textField"
                    label={localize("PLANNER_COURSE_AUDIENCE")}
                    multiline={true}
                    name="audienceDescription"
                    onChange={onTextFieldChanged}
                    required={false}
                    validateOnFocusIn={true}
                    validateOnFocusOut={true}
                    errorMessage={errors.audienceDescription}
                    value={model.audienceDescription || ""}
                />
            </FormControl>

            <FormControl>
                <TextField
                    aria-required={false}
                    className="textField"
                    label={localize("PLANNER_COURSE_INSTRUCTOR")}
                    multiline={true}
                    name="instructor"
                    onChange={onTextFieldChanged}
                    required={false}
                    validateOnFocusIn={true}
                    validateOnFocusOut={true}
                    errorMessage={errors.instructor}
                    value={model.instructor || ""}
                />
            </FormControl>

            <FormControl label={localize("PLANNER_COURSE_MATERIAL")} tooltipText={getAvailableFileTypes()}>

                <PlannerFilePicker
                    files={model.files}
                    fileTypes={PlannerFileTypes}
                    multiple={true}
                    itemType={model.itemType}
                    fieldType={SpintrTypes.PlannerCourseFileType.CourseMaterial}
                    onModelUpdate={onModelUpdate}
                    simple={false} />

            </FormControl>
            
            <FormControl>
                <TextField
                    aria-required={false}
                    className="textField"
                    label={localize("PLANNER_COURSE_PLAN")}
                    multiline={true}
                    name="coursePlan"
                    onChange={onTextFieldChanged}
                    required={false}
                    validateOnFocusIn={true}
                    validateOnFocusOut={true}
                    errorMessage={errors.coursePlan}
                    value={model.coursePlan || ""}
                />

                <PlannerFilePicker
                    files={model.files}
                    fileTypes={PlannerFileTypes}
                    multiple={true}
                    itemType={model.itemType}
                    fieldType={SpintrTypes.PlannerCourseFileType.CoursePlan}
                    onModelUpdate={onModelUpdate}
                    simple={true} />

            </FormControl>
            
            <FormControl>
                <TextField
                    aria-required={false}
                    className="textField"
                    label={localize("PLANNER_COURSE_EXAMINATION")}
                    multiline={true}
                    name="examinationMethod"
                    onChange={onTextFieldChanged}
                    required={false}
                    validateOnFocusIn={true}
                    validateOnFocusOut={true}
                    errorMessage={errors.examinationMethod}
                    value={model.examinationMethod || ""}
                />
            </FormControl>

            {/* TODO: Add Learning Hub Course */}
        </Fragment>
    );
}

export default PlannerCourseForm;
